import React from 'react';
import Icon from 'components/AppIcon';

const WellnessScoreCard = ({ 
  score = 0, 
  category = "Unknown", 
  riskLevel = "unknown",
  completionDate,
  totalQuestions = 0 
}) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'bg-success-50 border-success-100';
    if (score >= 60) return 'bg-warning-50 border-warning-100';
    return 'bg-error-50 border-error-100';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return 'CheckCircle';
    if (score >= 60) return 'AlertCircle';
    return 'XCircle';
  };

  const getScoreIconColor = (score) => {
    if (score >= 80) return 'var(--color-success)';
    if (score >= 60) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getInterpretation = (score) => {
    if (score >= 80) {
      return {
        title: "Excellent Wellness",
        description: "You're demonstrating strong wellness patterns with minimal areas of concern. Continue your current positive practices."
      };
    }
    if (score >= 60) {
      return {
        title: "Moderate Wellness",
        description: "You have a good foundation but there are some areas that could benefit from attention and improvement."
      };
    }
    return {
      title: "Wellness Concerns",
      description: "Several areas need attention. Consider implementing the recommended strategies and seeking professional support."
    };
  };

  const interpretation = getInterpretation(score);
  const formattedDate = new Date(completionDate).toLocaleDateString();

  return (
    <div className={`card ${getScoreBackground(score)}`}>
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-surface rounded-full mx-auto mb-4">
          <Icon 
            name={getScoreIcon(score)} 
            size={32} 
            color={getScoreIconColor(score)} 
          />
        </div>

        {/* Score Display */}
        <div className="mb-4">
          <div className={`text-4xl font-heading font-bold ${getScoreColor(score)} mb-2`}>
            {score}
          </div>
          <div className="text-sm text-text-secondary">
            Wellness Score (out of 100)
          </div>
        </div>

        {/* Category */}
        <div className="mb-6">
          <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
            {interpretation.title}
          </h3>
          <p className="text-sm text-text-secondary">
            {interpretation.description}
          </p>
        </div>

        {/* Progress Visualization */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-text-secondary mb-2">
            <span>Poor</span>
            <span>Excellent</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                score >= 80 ? 'bg-success' : score >= 60 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${score}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-text-secondary mt-1">
            <span>0</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>

        {/* Assessment Details */}
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Completed:</span>
            <span className="font-medium text-text-primary">{formattedDate}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Questions Answered:</span>
            <span className="font-medium text-text-primary">{totalQuestions}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Risk Level:</span>
            <span className={`font-medium capitalize ${getScoreColor(score)}`}>
              {riskLevel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessScoreCard;